// One place that talks to the API. Every call is same-origin, so the session
// cookie travels automatically and there is no token for JavaScript to hold
// or leak.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: { field: string; message: string }[],
  ) {
    super(message)
    this.name = 'ApiError'
  }
  /** Per-field messages, keyed for a form to render inline. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const d of this.details ?? []) out[d.field] = d.message
    return out
  }
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal }

let onUnauthenticated: (() => void) | null = null
export function setUnauthenticatedHandler(fn: () => void) { onUnauthenticated = fn }

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  let res: Response

  try {
    res = await fetch(path, {
      method,
      // Belt and braces: same-origin is already the default.
      credentials: 'same-origin',
      headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    // The offline case the architecture designs for: no silent retry, no
    // optimistic write. Say so plainly and let the person try again.
    throw new ApiError(0, 'OFFLINE', 'Could not reach the server. Check the connection and try again.')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? safeJson(text) : null

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: never } })?.error
    if (res.status === 401) onUnauthenticated?.()
    throw new ApiError(
      res.status,
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Something went wrong. Please try again.',
      err?.details,
    )
  }
  return payload as T
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text) } catch { return null }
}

export const get = <T,>(path: string, signal?: AbortSignal) => api<T>(path, { signal })
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body })
export const patch = <T,>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body })
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' })
